import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Navigate } from 'react-router-dom';
import Sidebar from '../components/layout/Sidebar';
import PageHeader from '../components/PageHeader';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { supabase } from '../lib/supabase';
import { useUser } from '../context/UserContext';
import { Button, DropdownOption } from '../components/ui';
import {
  StageColumn,
  DealCardPreview,
  StageDateModal,
  ProspectModal,
  SalesStats,
  SalesActionBar,
  SalesListView,
  AdsCloseRate,
  Prospect,
  ProspectInsert,
  Stage,
  ProspectStage,
} from '../components/sales';
import { isAdsLead, stageEnteredMs, AdsStats } from '../components/sales/meta';
import { timeframeRange, inDateRange, TimeframeKey, CustomRange } from '../components/sales/timeframe';
import DeleteConfirmationModal from '../components/clientDetail/sections/functions/DeleteConfirmationModal';

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

const STAGE_CONFIGS: Array<{ id: ProspectStage; name: string }> = [
  { id: 'leads', name: 'Leads' },
  { id: 'qualified', name: 'Qualified' },
  { id: 'proposal', name: 'Proposal' },
  { id: 'closed_won', name: 'Closed Won' },
  { id: 'closed_lost', name: 'Closed Lost' },
];

const STAGE_NAMES: Record<ProspectStage, string> = {
  leads: 'Leads',
  qualified: 'Qualified',
  proposal: 'Proposal',
  closed_won: 'Closed Won',
  closed_lost: 'Closed Lost',
};

/** Stages that count as "qualified" for the Google Ads Qualified Lead stamp. */
const QUALIFIED_OR_LATER: ProspectStage[] = ['qualified', 'proposal', 'closed_won'];

// Source filter values: 'all', 'ads' (any Google click id), or 'source:<text>' for a literal
// prospects.source value ('source:' alone = rows with no source).
const SOURCE_PREFIX = 'source:';
const sourceKey = (p: Prospect) => (p.source || '').trim();
const sourceLabel = (name: string) => (name ? name.charAt(0).toUpperCase() + name.slice(1) : 'No source');

export default function Sales() {
  const { user, isUserLoaded } = useUser();
  const [prospects, setProspects] = useState<Prospect[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'kanban' | 'list'>('kanban');
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [timeframe, setTimeframe] = useState<TimeframeKey>('this_month');
  const [customRange, setCustomRange] = useState<CustomRange>({ from: '', to: '' });
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  // Delete goes through the app's confirm modal, never the browser's. `fromPanel` closes the
  // edit drawer on success and lifts the modal above it.
  const [pendingDelete, setPendingDelete] = useState<{ id: string; fromPanel: boolean } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingMove, setPendingMove] = useState<{ before: Prospect; targetStageId: ProspectStage } | null>(null);
  const [isMoving, setIsMoving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProspect, setEditingProspect] = useState<Prospect | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [adsStats, setAdsStats] = useState<AdsStats | null>(null);

  const userRole = normalizeRole(user?.role);
  const isAdmin = isUserLoaded && userRole === 'admin';

  // Movement-only activation on mouse so plain clicks (the ⋮ menu, buttons) reach the card.
  // A `delay` constraint would start the drag mid-click and dnd-kit then swallows the click.
  // Long-press to drag on touch so list scrolling stays scrolling (same as AiQueueCards).
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 8 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 8 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch prospects from Supabase
  const fetchProspects = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('prospects')
        .select('*')
        .order('stage')
        .order('stage_entered_at', { ascending: false });

      if (error) throw error;
      // Row → Prospect: stage narrows to the stages the pipeline still uses; tags is never null.
      setProspects((data || []) as Prospect[]);
    } catch (err) {
      console.error('Error fetching prospects:', err);
      setError('Failed to load prospects');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchProspects();
    }
  }, [isAdmin, fetchProspects]);

  // The close rate Project Signal prices Qualified Leads at. Re-read whenever the cards change,
  // since a win or loss moves the running average.
  const fetchAdsStats = useCallback(async () => {
    const { data, error } = await supabase.from('prospects_ads_stats').select('*').maybeSingle();
    if (error) {
      console.error('Error fetching Google Ads close rate:', error);
      return;
    }
    setAdsStats(data);
  }, []);

  useEffect(() => {
    if (isAdmin) fetchAdsStats();
  }, [isAdmin, prospects, fetchAdsStats]);

  // Timeframe narrows Closed Won / Closed Lost only, by when the card closed (stage_entered_at,
  // the date the move modal confirms). Open stages always show every card. Everything below —
  // KPI strip, header count, source counts, board, list — works from this set.
  const inRange = useMemo(() => {
    const range = timeframeRange(timeframe, customRange);
    return prospects.filter(
      (p) => !p.stage.startsWith('closed') || inDateRange(p.stage_entered_at ?? p.created_at, range)
    );
  }, [prospects, timeframe, customRange]);

  // Source filter options, with counts, built from what's actually in the timeframe
  const sourceOptions = useMemo<DropdownOption[]>(() => {
    const counts = new Map<string, number>();
    for (const p of inRange) {
      const key = sourceKey(p);
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    const adsCount = inRange.filter(isAdsLead).length;
    const named = [...counts.keys()].filter(Boolean).sort((a, b) => a.localeCompare(b));
    const unknown = counts.get('') || 0;
    return [
      { value: 'all', label: 'All sources' },
      { value: 'ads', label: `Google Ads (${adsCount})` },
      ...named.map((name) => ({ value: SOURCE_PREFIX + name, label: `${sourceLabel(name)} (${counts.get(name)})` })),
      ...(unknown > 0 ? [{ value: SOURCE_PREFIX, label: `${sourceLabel('')} (${unknown})` }] : []),
    ];
  }, [inRange]);

  // Then by source, then by search term
  const filteredProspects = useMemo(() => {
    const bySource =
      sourceFilter === 'all'
        ? inRange
        : sourceFilter === 'ads'
          ? inRange.filter(isAdsLead)
          : inRange.filter((p) => sourceKey(p) === sourceFilter.slice(SOURCE_PREFIX.length));

    if (!searchTerm) return bySource;

    const term = searchTerm.toLowerCase();
    return bySource.filter(
      (prospect) =>
        prospect.company_name.toLowerCase().includes(term) ||
        prospect.contact_name.toLowerCase().includes(term) ||
        prospect.contact_email?.toLowerCase().includes(term) ||
        prospect.source?.toLowerCase().includes(term) ||
        prospect.tags.some((tag) => tag.toLowerCase().includes(term))
    );
  }, [inRange, sourceFilter, searchTerm]);

  // Organize prospects into stages
  const stages: Stage[] = useMemo(() => {
    return STAGE_CONFIGS.map((stageConfig) => ({
      ...stageConfig,
      prospects: filteredProspects
        .filter((p) => p.stage === stageConfig.id)
        .sort((a, b) => stageEnteredMs(b) - stageEnteredMs(a)),
    }));
  }, [filteredProspects]);

  const openDealsCount = inRange.filter((p) => !p.stage.startsWith('closed')).length;

  // Move a prospect to a stage (shared by drag-and-drop and Advance Stage)
  // Moving a card is two steps: it lands optimistically, then the date modal asks when the
  // move really happened. Confirm persists stage + stage_entered_at (+ the first-time Google Ads
  // stamps) with that date; cancel puts the card back.
  const moveProspectToStage = (prospectToMove: Prospect, targetStageId: ProspectStage) => {
    if (targetStageId === prospectToMove.stage) return;
    setProspects((prev) =>
      prev.map((p) =>
        p.id === prospectToMove.id ? { ...p, stage: targetStageId, stage_entered_at: new Date().toISOString() } : p
      )
    );
    setPendingMove({ before: prospectToMove, targetStageId });
  };

  const cancelMove = () => {
    if (!pendingMove || isMoving) return;
    const { before } = pendingMove;
    setProspects((prev) => prev.map((p) => (p.id === before.id ? before : p)));
    setPendingMove(null);
  };

  const commitMove = async (when: Date) => {
    if (!pendingMove || isMoving) return;
    const { before, targetStageId } = pendingMove;
    const iso = when.toISOString();
    // Mirror prospects_stamp_stage(): first entry into a stage sets its stamp, never overwrites.
    const stamps: Partial<Prospect> = {
      ...(QUALIFIED_OR_LATER.includes(targetStageId) && !before.qualified_at ? { qualified_at: iso } : {}),
      ...(targetStageId === 'closed_won' && !before.closed_won_at ? { closed_won_at: iso } : {}),
      ...(targetStageId === 'closed_lost' && !before.closed_lost_at ? { closed_lost_at: iso } : {}),
    };
    const updateData = { stage: targetStageId, stage_entered_at: iso, ...stamps };

    try {
      setIsMoving(true);
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) {
        setError('Authentication required. Please refresh the page and log in again.');
        await fetchProspects();
        return;
      }

      const { error } = await supabase.from('prospects').update(updateData).eq('id', before.id);
      if (error) {
        if (error.code === 'PGRST301' || error.message?.includes('permission') || error.message?.includes('policy')) {
          setError(
            'Permission denied: You can only move deals you created or are assigned to. Contact an admin if you need to move this deal.'
          );
        } else if (error.code === '42501') {
          setError('Database permission error: Please contact an administrator.');
        } else {
          setError(`Failed to update deal position: ${error.message || 'Unknown error'}`);
        }
        await fetchProspects();
        return;
      }
      setProspects((prev) => prev.map((p) => (p.id === before.id ? { ...p, ...updateData } : p)));
    } catch {
      await fetchProspects();
    } finally {
      setIsMoving(false);
      setPendingMove(null);
    }
  };

  const activeDragProspect = activeDragId ? (prospects.find((p) => p.id === activeDragId) ?? null) : null;

  const handleDragStart = (event: DragStartEvent) => setActiveDragId(String(event.active.id));

  // Handle drag and drop stage changes
  const handleDragEnd = async (event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    // Find the prospect being moved
    const prospectToMove = prospects.find((p) => p.id === active.id);
    if (!prospectToMove) return;

    let targetStageId: ProspectStage;

    // Determine target stage
    if (over.id.toString().startsWith('droppable-')) {
      // Dropped on stage column - use that stage
      targetStageId = over.id.toString().replace('droppable-', '') as ProspectStage;
    } else {
      // Dropped on another prospect
      const targetProspect = prospects.find((p) => p.id === over.id);
      if (!targetProspect) return;

      targetStageId = targetProspect.stage;
    }

    moveProspectToStage(prospectToMove, targetStageId);
  };

  // Advance a deal to the next pipeline stage
  const handleAdvanceProspect = async (prospect: Prospect) => {
    const idx = STAGE_CONFIGS.findIndex((s) => s.id === prospect.stage);
    if (idx === -1 || prospect.stage.startsWith('closed')) return;
    moveProspectToStage(prospect, STAGE_CONFIGS[idx + 1].id);
  };

  // Save prospect (create or update)
  const handleSaveProspect = async (prospectData: Partial<Prospect>) => {
    try {
      setIsSaving(true);

      if (editingProspect) {
        // Update existing prospect
        const { error } = await supabase
          .from('prospects')
          .update({
            ...prospectData,
            updated_at: new Date().toISOString(),
          })
          .eq('id', editingProspect.id);

        if (error) throw error;

        setProspects((prev) =>
          prev.map((p) =>
            p.id === editingProspect.id ? { ...p, ...prospectData, updated_at: new Date().toISOString() } : p
          )
        );
      } else {
        // Create new prospect. The drawer only enables Save with a company and a contact.
        const insert: ProspectInsert = {
          ...prospectData,
          company_name: prospectData.company_name ?? '',
          contact_name: prospectData.contact_name ?? '',
          created_by: user?.id,
          stage_entered_at: new Date().toISOString(),
        };
        const { data, error } = await supabase.from('prospects').insert([insert]).select().single();

        if (error) throw error;
        setProspects((prev) => [data as Prospect, ...prev]);
      }

      setIsModalOpen(false);
      setEditingProspect(null);
    } catch (err) {
      console.error('Error saving prospect:', err);
      setError('Failed to save deal');
    } finally {
      setIsSaving(false);
    }
  };

  // Delete from a card's ⋮ menu — ask first
  const handleDeleteProspect = (id: string) => setPendingDelete({ id, fromPanel: false });

  // Delete from inside the edit panel — ask first, close the panel on success
  const handleDeleteFromPanel = (id: string) => setPendingDelete({ id, fromPanel: true });

  const confirmDelete = async () => {
    if (!pendingDelete || isDeleting) return;
    const { id, fromPanel } = pendingDelete;
    try {
      setIsDeleting(true);
      const { error } = await supabase.from('prospects').delete().eq('id', id);
      if (error) throw error;
      setProspects((prev) => prev.filter((p) => p.id !== id));
      if (fromPanel) {
        setIsModalOpen(false);
        setEditingProspect(null);
      }
    } catch (err) {
      console.error('Error deleting prospect:', err);
      setError('Failed to delete deal');
    } finally {
      setIsDeleting(false);
      setPendingDelete(null);
    }
  };

  const pendingDeleteName = pendingDelete ? prospects.find((p) => p.id === pendingDelete.id)?.company_name : undefined;

  // Edit prospect
  const handleEditProspect = (prospect: Prospect) => {
    setEditingProspect(prospect);
    setIsModalOpen(true);
  };

  const handleAddDeal = () => {
    setEditingProspect(null);
    setIsModalOpen(true);
  };

  // Only admin users can access sales
  if (!isUserLoaded) {
    return null;
  }

  if (userRole !== 'admin') {
    return <Navigate to="/clients" replace />;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen">
        <Sidebar />
        <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
          <div className="flex items-center justify-center h-[60vh]">
            <div className="text-center">
              <div
                className="w-10 h-10 rounded-full animate-spin mx-auto"
                style={{
                  border: '3px solid var(--border-hairline)',
                  borderTopColor: 'var(--accent)',
                  marginBottom: 'var(--space-4)',
                }}
              />
              <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-faint)' }}>Loading...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Pipeline"
          count={openDealsCount}
          searchPlaceholder="Search deals..."
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          actions={
            <Button size="sm" icon="plus" onClick={handleAddDeal} style={{ marginLeft: '6px' }}>
              Add Deal
            </Button>
          }
        />

        {/* Error Display */}
        {error && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              marginBottom: 'var(--space-4)',
              padding: '10px 14px',
              borderRadius: 'var(--radius-md)',
              background: 'var(--status-down-soft)',
              border: '1px solid rgba(229, 72, 77, 0.25)',
              color: 'var(--danger-400)',
              fontSize: 'var(--text-sm)',
            }}
          >
            <span style={{ flex: 1 }}>{error}</span>
            <button
              onClick={() => setError(null)}
              className="pressable"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'inherit',
                fontFamily: 'var(--font-ui)',
                fontSize: 'var(--text-sm)',
                fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'],
                cursor: 'pointer',
                textDecoration: 'underline',
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Google Ads close rate + override */}
        <AdsCloseRate stats={adsStats} onChanged={fetchAdsStats} />

        {/* KPI strip */}
        <SalesStats prospects={inRange} />

        {/* Timeframe + source filters, view toggle */}
        <SalesActionBar
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          timeframe={timeframe}
          onTimeframeChange={setTimeframe}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
          sourceFilter={sourceFilter}
          sourceOptions={sourceOptions}
          onSourceFilterChange={setSourceFilter}
        />

        {viewMode === 'kanban' ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragCancel={() => setActiveDragId(null)}
          >
            {/* Phones: the board scrolls sideways inside its own strip, bleeding to the
                screen edges and snapping one column at a time. */}
            <div
              className="ck-kanban flex overflow-x-auto items-start -mx-4 px-4 scroll-px-4 snap-x snap-mandatory md:mx-0 md:px-0 md:scroll-px-0 md:snap-none"
              style={{ gap: 'var(--space-4)', paddingBottom: 'var(--space-4)', overscrollBehaviorX: 'contain' }}
            >
              {stages.map((stage) => (
                <SortableContext
                  key={stage.id}
                  items={stage.prospects.map((prospect) => prospect.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <StageColumn
                    stage={stage}
                    prospects={stage.prospects}
                    onEdit={handleEditProspect}
                    onDelete={handleDeleteProspect}
                    onAdvance={handleAdvanceProspect}
                  />
                </SortableContext>
              ))}
            </div>
            {/* The grabbed card follows the pointer above every column and scroll container. */}
            {createPortal(
              <DragOverlay>{activeDragProspect ? <DealCardPreview prospect={activeDragProspect} /> : null}</DragOverlay>,
              document.body
            )}
          </DndContext>
        ) : (
          <SalesListView prospects={filteredProspects} stages={stages} onEdit={handleEditProspect} />
        )}
      </main>

      {/* Add/Edit Deal Panel */}
      <ProspectModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingProspect(null);
        }}
        prospect={editingProspect}
        onSave={handleSaveProspect}
        isLoading={isSaving}
        onDelete={handleDeleteFromPanel}
        adsStats={adsStats}
      />

      <StageDateModal
        isOpen={pendingMove !== null}
        companyName={pendingMove?.before.company_name ?? ''}
        stageName={pendingMove ? STAGE_NAMES[pendingMove.targetStageId] : ''}
        isSaving={isMoving}
        onConfirm={commitMove}
        onCancel={cancelMove}
      />

      <DeleteConfirmationModal
        isOpen={pendingDelete !== null}
        onClose={() => {
          if (!isDeleting) setPendingDelete(null);
        }}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title="Delete Deal"
        message={
          pendingDeleteName
            ? `Delete ${pendingDeleteName}? This removes the deal and cannot be undone.`
            : 'Delete this deal? This cannot be undone.'
        }
        zIndex={pendingDelete?.fromPanel ? 100 : 50}
      />
    </div>
  );
}
